/**
 * Viewport Video Controller
 * ──────────────────────────────────────────────────────────────────────────
 * Automatically plays/pauses <video> elements as they enter/leave the
 * viewport using IntersectionObserver.
 *
 * Usage in Liquid:
 *   Add the class `js-viewport-video` to any <video> element.
 *   For Shopify video_tag filter: class: 'your-class js-viewport-video'
 *   For raw <video> elements: class="your-class js-viewport-video"
 *
 * Behaviour:
 *   - Enter viewport  → video.play()  (unless user manually paused it)
 *   - Leave viewport  → video.pause()
 *   - If user has clicked a paired pause button, viewport re-entry will NOT
 *     resume playback until the user clicks play again.
 *
 * Integration with pause buttons:
 *   When the user manually pauses/plays, set the attribute:
 *     video.dataset.manualPause = 'true'   // paused by user
 *     video.dataset.manualPause = 'false'  // playing by user choice
 *   viewport-video.js checks this flag before auto-resuming.
 *
 * Shopify customizer:
 *   On shopify:section:load, new videos in the re-rendered section are
 *   automatically observed. On shopify:section:unload, videos are
 *   unobserved to prevent stale references.
 *
 * ──────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  const SELECTOR = 'video.js-viewport-video';
  const THRESHOLD = 0.2; // at least 20% of video visible to trigger play

  let observer = null;

  function createObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          const video = entry.target;
          const manuallyPaused = video.dataset.manualPause === 'true';

          if (entry.isIntersecting) {
            if (!manuallyPaused) {
              video.play().catch(function () {
                // Autoplay blocked (rare since video is muted), silently ignore
              });
            }
          } else {
            if (!video.paused) {
              video.pause();
            }
          }
        });
      },
      { threshold: THRESHOLD }
    );
    return observer;
  }

  function observeVideos(root) {
    const obs = createObserver();
    const container = root || document;
    const videos = container.querySelectorAll(SELECTOR);
    videos.forEach(function (video) {
      if (!video.dataset.manualPause) {
        video.dataset.manualPause = 'false';
      }
      obs.observe(video);
    });
  }

  function unobserveVideos(root) {
    if (!observer) return;
    const container = root || document;
    const videos = container.querySelectorAll(SELECTOR);
    videos.forEach(function (video) {
      observer.unobserve(video);
    });
  }

  function initViewportVideo() {
    observeVideos(document);
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initViewportVideo);
  } else {
    initViewportVideo();
  }

  // Shopify Theme Editor events — observe new videos after section re-render
  document.addEventListener('shopify:section:load', function (e) {
    observeVideos(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    unobserveVideos(e.target);
  });

  // Expose for external use if needed
  window.__viewportVideo = { observeVideos: observeVideos, unobserveVideos: unobserveVideos };
})();
